import { Inject, Injectable, OnDestroy } from '@angular/core';
import { LOGGER_CONFIG, LOGGER_HTTP_SENDER } from './logger.tokens';
import { Batcher, createBatcher } from './logger.batcher';
import { sendBeacon } from './logger.beacon';
import { clearUser, getSessionId, getUser, setUser } from './logger.session';
import { LogBatchPayload, LogEvent, LogLevel, LoggerConfig, LoggerHttpSender, LogUser } from './logger.types';

const LEVEL_PRIORITY: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

const DEFAULTS = {
  flushIntervalMs: 5000,
  maxBatchSize: 20,
  maxQueueSize: 1000,
  minLevel: 'info' as LogLevel,
};

@Injectable({ providedIn: 'root' })
export class LoggerService implements OnDestroy {
  private readonly batcher: Batcher;
  private readonly minPriority: number;
  private readonly mirror: boolean;
  private sessionAnnounced = false;

  constructor(
    @Inject(LOGGER_CONFIG) private readonly config: LoggerConfig,
    @Inject(LOGGER_HTTP_SENDER) private readonly sender: LoggerHttpSender,
  ) {
    this.minPriority = LEVEL_PRIORITY[config.minLevel ?? DEFAULTS.minLevel];
    this.mirror = config.mirrorToConsole ?? false;

    this.batcher = createBatcher({
      flushIntervalMs: config.flushIntervalMs ?? DEFAULTS.flushIntervalMs,
      maxBatchSize: config.maxBatchSize ?? DEFAULTS.maxBatchSize,
      maxQueueSize: config.maxQueueSize ?? DEFAULTS.maxQueueSize,
      onFlush: (events) => this.sender.send(config.endpoint, this.buildPayload(events)),
    });

    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('pagehide', this.onPageHide);

    this.announceSessionStart();
  }

  private onVisibility = (): void => {
    if (document.visibilityState !== 'hidden') return;
    this.drainOnUnload();
  };
  private onPageHide = (): void => { this.drainOnUnload(); };

  private resolveBeaconUrl(): string {
    if (this.config.beaconEndpoint) return this.config.beaconEndpoint;
    const ep = this.config.endpoint;
    if (/^https?:\/\//.test(ep)) return ep;
    if (typeof window === 'undefined') return ep;
    return new URL(ep, window.location.origin).toString();
  }

  private drainOnUnload(): void {
    const drained = this.batcher.drain();
    if (drained.length === 0) return;
    const payload = this.buildPayload(drained);
    const ok = sendBeacon(this.resolveBeaconUrl(), payload);
    if (!ok) void this.sender.send(this.config.endpoint, payload);
  }

  private buildPayload(events: LogEvent[]): LogBatchPayload {
    const user = getUser();
    return {
      appName: this.config.appName,
      sessionId: getSessionId(),
      release: this.config.release,
      ...(user ? { user } : {}),
      events,
    };
  }

  private emit(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    if (LEVEL_PRIORITY[level] < this.minPriority) return;
    const base = this.config.getContext?.() ?? {};
    const merged = { ...base, ...(context ?? {}) };
    const event: LogEvent = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...(Object.keys(merged).length > 0 ? { context: merged } : {}),
    };
    if (this.mirror) {
      const m = level === 'debug' ? 'log' : level;
      // eslint-disable-next-line no-console
      (console as any)[m](`[${level}] ${message}`, merged);
    }
    this.batcher.push(event);
  }

  private announceSessionStart(): void {
    if (this.sessionAnnounced) return;
    this.sessionAnnounced = true;
    this.info('app.session.start');
  }

  debug(msg: string, ctx?: Record<string, unknown>) { this.emit('debug', msg, ctx); }
  info(msg: string, ctx?: Record<string, unknown>)  { this.emit('info',  msg, ctx); }
  warn(msg: string, ctx?: Record<string, unknown>)  { this.emit('warn',  msg, ctx); }
  error(msg: string, ctx?: Record<string, unknown>) { this.emit('error', msg, ctx); }

  setUser(user: LogUser) { setUser(user); }
  clearUser() { clearUser(); }
  flush(): Promise<void> { return this.batcher.flushNow(); }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', this.onPageHide);
    this.batcher.stop();
  }
}
